import { useLocale } from "../../hooks/useLocale";
import { msg } from "../../locales/translate";
import { Box, HBox } from "@ui/box/Box";
import Button from "@ui/button/Button";
import { Error } from "@ui/error/Error";
import Spacer from "@ui/spacer/Spacer";
import { Typo, TypoCaption } from "@ui/text/Typo";
import { useState } from "react";
import { GD } from "src/GD";
import { useMobile } from "src/hooks/useMobile";
import { FormParser } from "../FormParser";
import { StartScreenView } from "../StartScreenView";

const form = {
	name: "",
	phone: "",
	password: "",
	confirm_pssword: "",
};

type TNewUserForm = typeof form;

export const InvitePage = ({ code }: { code: string }) => {
	useLocale();
	const [busy, _setBusy] = useState(false);
	const [cancelled, setCancelled] = useState(false);
	const [error, setError] = useState<IError>();
	const { isMobile } = useMobile();

	const [invite] = GD.REQ_INVITE_GET.useRequest(undefined, { code: code });

	const getForm = () => {
		return { ...form };
	};

	const validate = (data: TNewUserForm): IError | undefined => {
		if (!data.name || !data.name) {
			return {
				code: 0,
				message: msg("emptyName"),
			};
		}
		if (!data.phone || !data.phone) {
			return {
				code: 0,
				message: msg("emptyPhone"),
			};
		}
		if (!data.password || !data.password) {
			return {
				code: 0,
				message: msg("emptyPassword"),
			};
		}
		if (data.password !== data.confirm_pssword) {
			return {
				code: 0,
				message: msg("thePasswordsDonTMatch"),
			};
		}
	};

	const callAccept = async () => {
		const res = await accept();
		if (res.error) {
			setError(res.error);
		} else {
			location.href = "/";
		}
	};

	const accept = async (form?: TNewUserForm) => {
		setError(undefined);
		if (form) {
			const invalid = validate(form);
			if (invalid) {
				return { error: invalid };
			}
		}
		const req = await GD.REQ_API.request({
			method: "users/invite/accept",
			data: {
				token: code,
				user: form
					? {
							name: form.name,
							phone: form.phone,
							password: form.password,
						}
					: null,
			},
		});

		return req;
	};

	const cancel = async () => {
		setError(undefined);
		const req = await GD.REQ_API.request({
			method: "users/invite/cancel",
			data: {
				token: code,
			},
		});
		if (req.error) {
			setError(req.error);
		} else {
			setCancelled(true);
		}
	};

	return (
		<StartScreenView busy={busy}>
			<Box
				sx={{
					padding: isMobile ? "24px 16px" : "60px",
					minWidth: isMobile ? "0" : "500px",
					width: isMobile ? "100%" : "auto",
					display: "flex",
					flexDirection: "column",
				}}>
				<Typo capitalize sx={{ fontSize: "26px", fontWeight: 400 }}>{msg("youHaveBeenInvited")}<br />{msg("toJoinOurSystem")}</Typo>

				<Spacer size="40px" />

				{invite &&
					(invite.error ? (
						<>
							<Spacer size="12px" />
							<Error error={invite.error} />
							<Spacer size="40px" />
						</>
					) : (
						<>
							{cancelled ? (
								<>
									<Typo variant="headMedium" sx={{ maxWidth: "350px" }}>{msg("invitationCancelled")}</Typo>
									<Spacer size="20px" />
								</>
							) : (
								<>
									<Typo variant="text" sx={{ maxWidth: "350px" }}>
										<>{msg("chooseAcceptButtonToJoinTheProject")}</>
										<b>{invite.data.invite.project_name} </b>
										<>{msg("invitedBy")}</>
										<>
											<b>{invite.data.invite.author_name ?? msg("admin")} </b>
										</>
										<>{msg("orClickOnCancelButtonToDecline")}</>
									</Typo>
									{!invite.data.user_registered && (
										<>
											<Spacer size="20px" />
											<FormParser
												key={"inviteForm"}
												fullSizeButtons={true}
												ignoreObserver={true}
												secondaryButton={{ title: msg("cancel"), variant: "primary", sx: { flex: "1" }, onClick: cancel }}
												primaryButton={{ title: msg("accept"), variant: "primary", sx: { flex: "1" } }}
												onSubmit={async (data: TNewUserForm): Promise<any> => {
													const res = await accept(data);
													if (res.error) {
														setError(res.error);
													} else {
														location.href = "/";
													}
													return res;
												}}
												onRequest={async (): Promise<any> => {
													return getForm();
												}}
											/>
										</>
									)}

									{invite.data.user_registered && (
										<>
											{error && (
												<>
													<Spacer size="20px" />
													<Error error={error} />
												</>
											)}
											<Spacer size="20px" />

											<HBox gap="10px" sx={{ width: "100%" }}>
												<Button variant="secondary" onClick={cancel} sx={{ flex: 1 }}>{msg("cancel")}</Button>
												<Button variant="primary" onClick={callAccept} sx={{ flex: 1 }}>{msg("accept")}</Button>
											</HBox>
										</>
									)}
									<Box
										sx={{
											display: "flex",
											gap: "5px",
											alignItems: "center",
											margin: "25px 0",
										}}>
										<Box
											sx={{
												border: "1px solid rgba(0,0,0,0.14)",
												width: "100%",
											}}
										/>
										<TypoCaption italic>{msg("or")}</TypoCaption>
										<Box
											sx={{
												border: "1px solid rgba(0,0,0,0.14)",
												width: "100%",
											}}
										/>
									</Box>
								</>
							)}
						</>
					))}

				<Button
					sx={{
						width: "auto",
					}}
					variant="secondary"
					onClick={() => (location.href = "/")}>{msg("signIn")}</Button>
			</Box>
		</StartScreenView>
	);
};
